import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, Image, X, Copy, Check } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface BlogImageUploadProps {
  onImageInsert?: (imageUrl: string, altText: string) => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface UploadedImage {
  id: string;
  name: string;
  url: string;
  created_at: string;
  size: number;
}

const BlogImageUpload = ({ onImageInsert, trigger, open: controlledOpen, onOpenChange: controlledOnOpenChange }: BlogImageUploadProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const [uploading, setUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const loadImages = async () => {
    setLoadingImages(true);
    try {
      const { data, error } = await supabase.storage
        .from('blog-images')
        .list('', {
          limit: 50,
          offset: 0,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) throw error;

      const imagesWithUrls: UploadedImage[] = [];
      for (const file of data || []) {
        const { data: urlData } = supabase.storage
          .from('blog-images')
          .getPublicUrl(file.name);
        
        imagesWithUrls.push({
          id: file.id || file.name,
          name: file.name,
          url: urlData.publicUrl,
          created_at: file.created_at || new Date().toISOString(),
          size: file.metadata?.size || 0
        });
      }

      setUploadedImages(imagesWithUrls);
    } catch (error) {
      console.error('Error loading images:', error);
      toast({
        title: "Error",
        description: "Failed to load images",
        variant: "destructive"
      });
    } finally {
      setLoadingImages(false);
    }
  };

  const handleOpen = (open: boolean) => {
    if (controlledOnOpenChange) {
      controlledOnOpenChange(open);
    } else {
      setInternalOpen(open);
    }
    if (open) {
      loadImages();
    }
  };

  const uploadImage = async (file: File) => {
    try {
      setUploading(true);
      
      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('blog-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      toast({
        title: "Success",
        description: "Image uploaded successfully"
      });

      // Reload images
      await loadImages();
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Error",
        description: "Please select an image file",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "Image must be smaller than 5MB",
        variant: "destructive"
      });
      return;
    }

    uploadImage(file);
  };

  const copyImageUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
      toast({
        title: "Copied!",
        description: "Image URL copied to clipboard"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy URL",
        variant: "destructive"
      });
    }
  };

  const insertImage = (imageUrl: string) => {
    const altText = prompt("Enter alt text for the image (optional):");
    const markdownImage = `![${altText || 'Blog image'}](${imageUrl})`;
    
    if (onImageInsert) {
      onImageInsert(markdownImage, altText || 'Blog image');
    } else {
      // Copy markdown to clipboard as fallback
      navigator.clipboard.writeText(markdownImage);
      toast({
        title: "Image Markdown Copied",
        description: "Paste it into your blog content"
      });
    }
    
    handleOpen(false);
  };

  const deleteImage = async (fileName: string) => {
    if (!confirm('Are you sure you want to delete this image?')) return;

    try {
      const { error } = await supabase.storage
        .from('blog-images')
        .remove([fileName]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Image deleted successfully"
      });

      await loadImages();
    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        title: "Error",
        description: "Failed to delete image",
        variant: "destructive"
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <Image className="w-4 h-4 mr-2" />
            Manage Images
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Blog Images</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Upload Section */}
          <div className="border-2 border-dashed border-border rounded-lg p-6">
            <div className="text-center">
              <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <div className="space-y-2">
                <Label htmlFor="image-upload" className="cursor-pointer">
                  <span className="text-lg font-medium">Upload New Image</span>
                  <p className="text-sm text-muted-foreground">
                    Click to select or drag and drop (Max 5MB, JPG/PNG/WEBP)
                  </p>
                </Label>
                <Input
                  ref={fileInputRef}
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  disabled={uploading}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading && <Upload className="w-4 h-4 mr-2 animate-spin" />}
                  {uploading ? 'Uploading...' : 'Select Image'}
                </Button>
              </div>
            </div>
          </div>

          {/* Images Grid */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Uploaded Images</h3>
            {loadingImages ? (
              <div className="text-center py-8">Loading images...</div>
            ) : uploadedImages.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No images uploaded yet
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {uploadedImages.map((image) => (
                  <Card key={image.id} className="overflow-hidden">
                    <div className="aspect-video relative">
                      <img
                        src={image.url}
                        alt={image.name}
                        className="w-full h-full object-cover"
                      />
                      <Button
                        size="sm"
                        variant="destructive"
                        className="absolute top-2 right-2 h-6 w-6 p-0"
                        onClick={() => deleteImage(image.name)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        <p className="text-sm font-medium truncate" title={image.name}>
                          {image.name}
                        </p>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {formatFileSize(image.size)}
                          </Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => copyImageUrl(image.url)}
                          >
                            {copiedUrl === image.url ? (
                              <Check className="w-3 h-3 mr-1" />
                            ) : (
                              <Copy className="w-3 h-3 mr-1" />
                            )}
                            Copy URL
                          </Button>
                          <Button
                            size="sm"
                            className="flex-1"
                            onClick={() => insertImage(image.url)}
                          >
                            Insert
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BlogImageUpload;